/**
 * useAuth - minimal client-side auth state hook (Day-1 SPA, JWT in localStorage)
 *
 * Re-renders subscribers when token changes via:
 *   - cross-tab `storage` event (browser fires on other tabs)
 *   - same-tab custom `asklaw:auth` event (we dispatch on login/logout)
 *
 * Also exposes `profile` - decoded JWT payload (sub/email/name/picture)
 * - for header avatar + /me account card. Profile is best-effort UX
 * data; never used for authz decisions (server verifies token per req).
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { decodeJwtPayload, type JwtProfile } from './lib/jwt';

const TOKEN_KEY = 'asklaw_access_token';
const REFRESH_KEY = 'asklaw_refresh_token';
const ISSUED_KEY = 'asklaw_token_issued_at';
const AUTH_EVENT = 'asklaw:auth';

export type AuthState = {
  isAuthenticated: boolean;
  accessToken: string | null;
  profile: JwtProfile | null;
};

function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function useAuth(): AuthState & { logout: () => void } {
  const [token, setToken] = useState<string | null>(() => readToken());

  useEffect(() => {
    const sync = () => setToken(readToken());
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY || e.key === null) sync();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_EVENT, sync);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_EVENT, sync);
    };
  }, []);

  const logout = useCallback(() => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(REFRESH_KEY);
      window.localStorage.removeItem(ISSUED_KEY);
    } catch {
      // ignore
    }
    window.dispatchEvent(new Event(AUTH_EVENT));
  }, []);

  const profile = useMemo(() => decodeJwtPayload(token), [token]);

  return {
    isAuthenticated: Boolean(token),
    accessToken: token,
    profile,
    logout,
  };
}

/** Dispatch after writing token to localStorage so same-tab subscribers re-render. */
export function notifyAuthChanged(): void {
  window.dispatchEvent(new Event(AUTH_EVENT));
}
